import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';

const NavigationDropdown = ({ 
  navigation = [], 
  className = '', 
  dropdownItems = {}
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const { t } = useTranslation(); // Use the translation hook for reactive updates

  // Generate dropdown items with translations
  const getTranslatedDropdownItems = () => {
    return {
      'shows': [
        { label: t('shows.current_season', 'Current Season'), url: '/shows/current/' },
        { label: t('shows.archive', 'Archive'), url: '/shows/archive/' }
      ],
      'about': [
        { label: t('about.company', 'The Company'), url: '/about/' },
        { label: t('about.mission', 'Mission & History'), url: '/about/#mission' },
        { label: t('about.resources', 'Resources'), url: '/resources/' }
      ],
      'resources': [
        { label: t('Resources', 'Resources'), url: '/resources/' },
        { label: t('Contacts', 'Contacts'), url: '/contacts/' }
      ],
      'world': [
        { label: t('Africa', 'Africa'), url: '/tag/africa/' },
        { label: t('Asia', 'Asia'), url: '/tag/asia/' },
        { label: t('America', 'America'), url: '/tag/america/' }
      ]
    };
  };

  // Translate navigation label based on common patterns
  const translateNavLabel = (label, slug) => {
    const lowerLabel = label.toLowerCase();
    const lowerSlug = (slug || '').toLowerCase();
    
    // Map common navigation labels to translation keys
    const labelMap = {
      'home': 'nav.home',
      'início': 'nav.home',
      'about': 'nav.about',
      'sobre': 'nav.about',
      'shows': 'nav.shows',
      'espetáculos': 'nav.shows',
      'espetaculos': 'nav.shows',
      'listen': 'nav.listen',
      'ouvir': 'nav.listen',
      'resources': 'nav.resources',
      'recursos': 'nav.resources',
      'contacts': 'nav.contacts',
      'contactos': 'nav.contacts',
      'contact': 'nav.contacts',
      'podcasts': 'nav.podcasts',
      'podcast': 'nav.podcasts',
      'gallery': 'nav.gallery',
      'galeria': 'nav.gallery'
    };

    // Try to find translation key by label or slug
    const translationKey = labelMap[lowerLabel] || labelMap[lowerSlug];
    
    if (translationKey) {
      return t(translationKey, label);
    }
    
    return label; // Return original if no translation found
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = (navItem) => {
    // Only handle hover on desktop
    if (window.innerWidth <= 768) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (hasDropdown(navItem)) {
      setActiveDropdown(navItem);
    }
  };

  const handleMouseLeave = () => {
    // Only handle hover on desktop
    if (window.innerWidth <= 768) return;
    
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handleClick = (e, navItem) => {
    // Handle clicks on mobile for dropdown toggle
    if (window.innerWidth <= 768 && hasDropdown(navItem)) {
      e.preventDefault();
      setActiveDropdown(activeDropdown?.label === navItem.label ? null : navItem);
    }
  };

  const getDropdownKey = (navItem) => {
    const itemKey = navItem.label.toLowerCase().replace(/\s+/g, '-');
    const itemSlug = navItem.slug ? navItem.slug.toLowerCase() : '';
    
    if (itemKey === 'shows' || itemSlug === 'shows' || 
        navItem.label.toLowerCase().includes('show') || 
        itemKey === 'espetáculos' || itemKey === 'espetaculos') {
      return 'shows';
    }
    
    if (itemKey === 'about' || itemSlug === 'about' || 
        itemKey === 'sobre' || itemKey === 'sobre-nos') {
      return 'about';
    }

    if (itemKey === 'resources' || itemKey === 'recursos' || itemSlug === 'resources') {
      return 'resources';
    }

    if (
      itemKey === 'world' ||
      itemKey === 'earth' ||
      itemKey === 'terra' ||
      itemSlug === 'world' ||
      itemSlug === 'earth' ||
      itemSlug === 'terra'
    ) {
      return 'world';
    }
    
    return null;
  };

  const getDropdownItems = (navItem) => {
    if (Array.isArray(navItem.children) && navItem.children.length > 0) {
      return navItem.children.map((item) => ({
        ...item,
        label: t(item.label, item.label)
      }));
    }

    const key = getDropdownKey(navItem);
    if (!key) return [];
    
    const translatedDropdownItems = getTranslatedDropdownItems();
    return translatedDropdownItems[key] || [];
  };

  const hasDropdown = (navItem) => {
    const items = getDropdownItems(navItem);
    return items.length > 0;
  };

  const getPathname = (url) => {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/index\/?$/, '/');
    } catch (e) {
      return url || '';
    }
  };

  if (!navigation || navigation.length === 0) {
    return null;
  }

  return (
    <div className={`navigation-dropdown-react ${className}`} ref={dropdownRef}>
      <ul className="nav-react">
        {navigation.map((item, index) => {
          const hasDropdownItems = hasDropdown(item);
          const isActive = activeDropdown && activeDropdown.label === item.label;
          
          return (
            <li 
              key={index}
              className={`nav-item-react ${hasDropdownItems ? 'has-dropdown' : ''} ${isActive ? 'active' : ''}`}
              onMouseEnter={() => handleMouseEnter(item)}
              onMouseLeave={handleMouseLeave}
            >
              <a 
                href={item.url} 
                className="nav-link-react"
                aria-haspopup={hasDropdownItems ? 'true' : undefined}
                aria-expanded={isActive ? 'true' : 'false'}
                aria-current={
                  (getPathname(window.location.pathname).startsWith(getPathname(item.url)) && getPathname(item.url) !== '/')
                    || getPathname(window.location.pathname) === getPathname(item.url)
                    ? 'page' : undefined
                }
                onClick={(e) => handleClick(e, item)}
              >
                {translateNavLabel(item.label, item.slug)}
                {hasDropdownItems && (
                  <span 
                    className={`nav-expand-indicator ${isActive ? 'open' : ''}`}
                    aria-hidden="true"
                  >
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                )}
              </a>
              
              {hasDropdownItems && (
                <div className="nav-dropdown-react" role="menu">
                  {getDropdownItems(item).map((dropdownItem, dropIndex) => (
                    <a 
                      key={dropIndex}
                      href={dropdownItem.url}
                      className="dropdown-link-react"
                      role="menuitem"
                    >
                      {dropdownItem.label}
                    </a>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default NavigationDropdown;